import { createContext, useState, useEffect, type ReactNode } from "react";
import type { AuthContextType, User } from "@/types/auth";
import { getCurrentUser, refreshSession } from "@/services/auth";
import { setAccessToken } from "@/lib/axiosClient";

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {

    // 1. STATE MURNI (Di Memori Saja)
    // Default null. Tidak ada baca-baca localStorage.
    const [user, setUserState] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Helper: Set User & Token di Memori
    const setAuthSuccess = (newUser: User, token: string) => {
        setAccessToken(token); // Simpan ke variable axiosClient
        setUserState(newUser); // Simpan ke state React
    };

    // Helper: Set User (untuk logout dsb)
    const setUser = (newUser: User | null) => {
        if (!newUser) {
            setAccessToken(null);
        }
        setUserState(newUser);
    };

    // 2. INITIALIZATION (Saat Refresh / Tab Baru)
    // Karena tidak ada localStorage, kita tanya ke Server via Cookie: "Saya masih login gak?"
    useEffect(() => {
        const initAuth = async () => {
            try {
                // Backend cek HttpOnly Cookie
                const token = await refreshSession();

                if (token) {
                    setAccessToken(token);
                    const userData = await getCurrentUser();

                    if (userData) {
                        setUserState(userData);
                    } else {
                        setUser(null);
                    }
                } else {
                    // Cookie expired / tidak ada
                    setUser(null);
                }
            } catch (error) {
                console.error("Inisialisasi auth gagal:", error);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    // 3. SINKRONISASI LOGOUT ANTAR TAB (Pakai BroadcastChannel)
    // Ini pengganti LocalStorage Event. Aman karena lewat jalur memori.
    useEffect(() => {
        const channel = new BroadcastChannel("auth_channel");

        channel.onmessage = (event) => {
            if (event.data === "LOGOUT_SIGNAL") {
                console.log("Tab lain logout. Membersihkan sesi tab ini...");

                // Bersihkan memori lokal
                setAccessToken(null);
                setUserState(null);
                // Otomatis PrivateRoute akan menendang ke login
            }
        };

        return () => {
            channel.close();
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                setAuthSuccess,
                isAuthenticated: !!user,
                loading,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};